import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type NotificationType = 'pending' | 'processing' | 'success' | 'failed';

export interface Notification {
  id: string;
  message: string;
  type: NotificationType;
  deviceId?: string;
  datapoint?: string;
  timestamp: number;
  duration?: number; // Optional custom duration in milliseconds
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => string;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

interface NotificationProviderProps {
  children: ReactNode;
  autoRemoveTime?: number; // in milliseconds
}

export function NotificationProvider({
  children,
  autoRemoveTime = 120000
}: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Auto-remove notifications after specified time
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    notifications.forEach((notification) => {
      // Use custom duration if provided, otherwise use the default
      const duration = notification.duration || autoRemoveTime;

      const timer = setTimeout(() => {
        removeNotification(notification.id);
      }, duration);

      timers.push(timer);
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [notifications, autoRemoveTime]);

  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp'>): string => {
    const id = Math.random().toString(36).substring(2, 9);
    const timestamp = Date.now();

    setNotifications(prev => {
      // Add new notification without any limit
      const updated = [{ ...notification, id, timestamp }, ...prev];
      return updated;
    });

    return id;
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        removeNotification,
        clearNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
